import React from "react";
import {Chart as ChartJS, ArcElement, Tooltip, Legend} from "chart.js";
import {Doughnut} from "react-chartjs-2";
import PropTypes from "prop-types";
import Colors from "./Colors";

ChartJS.register(ArcElement, Tooltip, Legend);

const WasteVolumeFraction = ({data}) => {
  if (!data) {
    return (
      <div className="card">
        <div className="card-header">
          <h5 className="m-0">Contenedores por Volumen</h5>
        </div>
        <div className="card-body">
          <div className="text-center">Cargando datos...</div>
        </div>
      </div>
    );
  }

  const {title, volumes} = data;

  const volumeKeys = Object.keys(volumes);
  const volumeValues = volumeKeys.map((volume) => volumes[volume].total);
  const volumeColors = volumeKeys.map(
    (_, idx) => `hsla(${(idx * 130) % 360}, 70%, 65%, 0.9)`
  );

  const volumeBorderColors = volumeKeys.map(
    (_, idx) => `hsla(${(idx * 130) % 360}, 70%, 55%, 1)`
  );

  const wasteTypesMap = {};

  volumeKeys.forEach((volume) => {
    const wasteTypes = volumes[volume].waste_types;
    Object.keys(wasteTypes).forEach((type) => {
      if (!wasteTypesMap[type]) {
        wasteTypesMap[type] = {
          count: 0,
          name: wasteTypes[type].name,
        };
      }
      wasteTypesMap[type].count += wasteTypes[type].count;
    });
  });

  // Preparar datos para el anillo exterior (tipos de residuos)
  const wasteTypeKeys = Object.keys(wasteTypesMap);
  const wasteTypeValues = wasteTypeKeys.map(
    (type) => wasteTypesMap[type].count
  );
  const wasteTypeLabels = wasteTypeKeys.map((type) => wasteTypesMap[type].name);
  const wasteTypeColors = wasteTypeKeys.map((type) =>
    Colors.getBackgroundColor
      ? Colors.getBackgroundColor(type)
      : `hsla(${(wasteTypeKeys.indexOf(type) * 60) % 360}, 85%, 60%, 0.9)`
  );
  const wasteTypeBorderColors = wasteTypeKeys.map((type) =>
    Colors.getBorderColor
      ? Colors.getBorderColor(type)
      : `hsla(${(wasteTypeKeys.indexOf(type) * 60) % 360}, 85%, 50%, 1)`
  );

  // Configuración del gráfico de dona anidado
  const chartData = {
    datasets: [
      {
        // Anillo exterior (tipos de residuos)
        data: wasteTypeValues,
        backgroundColor: wasteTypeColors,
        borderColor: "#ffffff",
        borderWidth: 2,
        weight: 1.5,
        label: "Tipos de Residuos",
      },
      {
        // Anillo interior (volúmenes)
        data: volumeValues,
        backgroundColor: volumeColors,
        borderColor: "#ffffff",
        borderWidth: 2,
        weight: 1,
        label: "Volúmenes",
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          generateLabels: (chart) => {
            // Generar etiquetas para tipos de residuos (dataset 0)
            const wasteLabels = wasteTypeLabels.map((label, i) => ({
              text: `${label} (${wasteTypeValues[i]})`,
              fillStyle: wasteTypeColors[i],
              strokeStyle: wasteTypeBorderColors[i],
              lineWidth: 1,
              hidden: false,
              index: i,
              datasetIndex: 0,
            }));

            // Generar etiquetas para volúmenes (dataset 1)
            const volumeLabels = volumeKeys.map((volume, i) => ({
              text: `${volume}L (${volumeValues[i]})`,
              fillStyle: volumeColors[i],
              strokeStyle: volumeBorderColors[i],
              lineWidth: 1,
              hidden: false,
              index: i,
              datasetIndex: 1,
            }));

            return [
              ...wasteLabels.map((label) => ({
                ...label,
                text: label.text,
              })),

              ...volumeLabels.map((label) => ({
                ...label,
                text: label.text,
              })),
            ];
          },
          boxWidth: 15,
          padding: 10,
          font: {
            size: 12,
          },
        },
      },
      title: {
        display: true,
        text: title,
        font: {
          size: 16,
        },
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems) => {
            const item = tooltipItems[0];
            return item.datasetIndex === 0 ? "Tipo de Residuo" : "Volumen";
          },
          label: (context) => {
            const datasetIndex = context.datasetIndex;
            const index = context.dataIndex;
            const value = context.raw;

            if (datasetIndex === 0) {
              // Tipos de residuos (anillo exterior)
              const type = wasteTypeKeys[index];
              return `${wasteTypesMap[type].name}: ${value} contenedores`;
            } else {
              // Volúmenes (anillo interior)
              const volume = volumeKeys[index];
              return `${volume}L: ${value} contenedores`;
            }
          },
        },
      },
    },
  };

  return (
    <div className="mb-4">
      <div className="border pb-3" style={{height: "380px"}}>
        <Doughnut data={chartData} options={chartOptions} />
      </div>
    </div>
  );
};

export default WasteVolumeFraction;
